import React, { useState } from 'react';
import { 
  HelpCircle, 
  AlertTriangle, 
  ShieldAlert, 
  Database, 
  X, 
  Search, 
  Terminal, 
  Server, 
  Globe, 
  Mail, 
  Phone, 
  Clock, 
  Users, 
  FileText, 
  ChevronRight,
  ArrowLeft,
  Lock,
  Settings,
  Zap,
  Info
} from 'lucide-react';

const HelpCenter = ({ showHelpPopup, setShowHelpPopup }) => {
  const [activeSection, setActiveSection] = useState('main');
  const [searchQuery, setSearchQuery] = useState('');
  const [previousSections, setPreviousSections] = useState([]);

  const navigateTo = (section) => {
    setPreviousSections([...previousSections, activeSection]);
    setActiveSection(section);
  };

  const navigateBack = () => {
    if (previousSections.length > 0) {
      const previous = previousSections[previousSections.length - 1];
      setPreviousSections(previousSections.slice(0, -1));
      setActiveSection(previous);
    }
  };

  if (!showHelpPopup) return null;

  const renderSection = () => {
    switch(activeSection) {
      case 'main':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {helpSections.map((section) => (
              <div 
                key={section.id}
                onClick={() => navigateTo(section.id)}
                className="flex items-start p-4 bg-gray-800/40 hover:bg-gray-800/70 border border-gray-700 rounded-lg cursor-pointer transition-colors duration-200"
              >
                <div className="flex-shrink-0 mr-3 mt-1">
                  <div className="p-2 rounded-full bg-gray-700/50">
                    {section.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-1">{section.title}</h3>
                  <p className="text-gray-400 text-sm">{section.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 self-center flex-shrink-0" />
              </div>
            ))}
          </div>
        );
      
      case 'tool-guides':
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-bold text-white mb-4">Tool Guides</h2>
            
            {toolGuides.map((guide) => (
              <div 
                key={guide.id}
                onClick={() => navigateTo(`guide-${guide.id}`)}
                className="flex items-center space-x-3 p-4 bg-gray-800/40 hover:bg-gray-800/70 border border-gray-700 rounded-lg cursor-pointer transition-colors duration-200"
              >
                <div className="p-2 rounded-full bg-gray-700/50">
                  {guide.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium">{guide.title}</h3>
                  <p className="text-gray-400 text-sm">{guide.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            ))}
          </div>
        );
      
      case 'guide-email':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Email Analysis Guide</h2>
            
            <div className="p-4 bg-blue-900/20 border border-blue-800/40 rounded-lg">
              <h3 className="flex items-center text-blue-400 font-medium mb-2">
                <Info className="w-5 h-5 mr-2" />
                How Email Analysis Works
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                The Email Analysis tool examines email headers, domain information, and sender patterns to provide insights about the email's origin and authenticity. Our system scans multiple databases to verify legitimacy and detect potential spoofing.
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg text-white font-medium">Using Email Analysis</h3>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 bg-gray-700 text-blue-400 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <h4 className="text-white font-medium">Enter the target email address</h4>
                    <p className="text-gray-400 text-sm">
                      Input the complete email address you want to analyze in the search field. Our system requires the full address including the domain (e.g., example@domain.com).
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 bg-gray-700 text-blue-400 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <h4 className="text-white font-medium">Run the analysis</h4>
                    <p className="text-gray-400 text-sm">
                      Click the "Run Analysis" button to initiate the scanning process. The system will search through multiple databases including DNS records, WHOIS information, and public email directories.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 bg-gray-700 text-blue-400 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <h4 className="text-white font-medium">Review the results</h4>
                    <p className="text-gray-400 text-sm">
                      After processing, the system will display findings including domain age, server information, previous breaches, and confidence rating for authenticity.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h3 className="text-lg text-white font-medium">Available Data Points</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {emailDataPoints.map((point, index) => (
                  <div key={index} className="flex items-start p-3 bg-gray-800/30 border border-gray-700 rounded-lg">
                    <div className="flex-shrink-0 mr-2 text-blue-400">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-white text-sm font-medium">{point.title}</h4>
                      <p className="text-gray-400 text-xs">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4 bg-yellow-900/20 border border-yellow-800/40 rounded-lg">
              <h3 className="flex items-center text-yellow-400 font-medium mb-2">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Limitations
              </h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Email headers can be modified or spoofed by sophisticated actors</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Privacy-focused email providers may limit available information</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Temporary or recently created domains may have limited history</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Results should be cross-verified with additional sources for critical decisions</span>
                </li>
              </ul>
            </div>
          </div>
        );
      
      case 'data-sources':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Data Sources</h2>
            <p className="text-gray-300">
              TraceMaster aggregates information from numerous public and proprietary databases to provide comprehensive analysis. Our system continuously updates its data connections to maintain accuracy.
            </p>
            
            <div className="space-y-4">
              {dataSources.map((category) => (
                <div key={category.title} className="space-y-3">
                  <h3 className="text-lg text-white font-medium flex items-center">
                    {category.icon}
                    <span className="ml-2">{category.title}</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {category.sources.map((source, index) => (
                      <div key={index} className="flex items-start p-3 bg-gray-800/30 border border-gray-700 rounded-lg">
                        <div className="flex-1">
                          <h4 className="text-white text-sm font-medium">{source.name}</h4>
                          <p className="text-gray-400 text-xs mt-1">{source.description}</p>
                          {source.limitations && (
                            <p className="text-yellow-500 text-xs mt-1">Limitation: {source.limitations}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 ml-3 px-2 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300">
                          {source.updateFrequency}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-4 bg-blue-900/20 border border-blue-800/40 rounded-lg">
              <h3 className="text-blue-400 font-medium mb-2">Data Freshness</h3>
              <p className="text-gray-300 text-sm">
                Our system displays the last update timestamp for each data source. Results from sources updated less frequently should be treated with appropriate caution.
              </p>
            </div>
          </div>
        );
      
      case 'legal-compliance':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Legal & Compliance Information</h2>
            
            <div className="p-4 bg-purple-900/20 border border-purple-800/40 rounded-lg">
              <h3 className="flex items-center text-purple-400 font-medium mb-2">
                <Lock className="w-5 h-5 mr-2" />
                Privacy Compliance
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                TraceMaster is designed to comply with applicable privacy regulations. We only access and analyze publicly available information or data that has been lawfully obtained from authorized sources.
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg text-white font-medium">Acceptable Usage</h3>
              
              <div className="space-y-3">
                <div className="flex items-start p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                  <div className="flex-shrink-0 mr-3 text-green-400">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Permitted Uses</h4>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span>Legitimate cybersecurity research and threat analysis</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span>Verification of suspicious communications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span>Educational purposes and security awareness training</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span>Preliminary information gathering for authorized investigations</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
                  <div className="flex-shrink-0 mr-3 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Prohibited Uses</h4>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        <span>Harassment, stalking, or any form of targeted intimidation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        <span>Unauthorized surveillance of individuals</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        <span>Illegal activities or circumventing legal protections</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        <span>Discrimination or profiling based on protected characteristics</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="text-white font-medium mb-2">For Law Enforcement</h3>
              <p className="text-gray-400 text-sm">
                Law enforcement agencies seeking comprehensive digital evidence should follow proper legal channels. TraceMaster's analysis should be considered supplementary to official investigative techniques.
              </p>
              <button
                onClick={() => navigateTo('law-enforcement')}
                className="mt-3 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors duration-200"
              >
                Law Enforcement Guidelines
              </button>
            </div>
          </div>
        );
        
      default:
        if (activeSection.startsWith('guide-')) {
          const guideId = activeSection.replace('guide-', '');
          const guide = toolGuides.find(g => g.id === guideId);
          if (guide) {
            return (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-white">{guide.title}</h2>
                <p className="text-gray-300">{guide.fullDescription || "Detailed guide content will appear here."}</p>
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-sm">
                    Full documentation for this tool is available in our comprehensive guides section.
                  </p>
                </div>
              </div>
            );
          }
        }
        return (
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 text-center">
            <p className="text-gray-300">Content for this section is being updated.</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md overflow-y-auto py-6">
      <div className="relative bg-gray-900 border border-gray-800 rounded-xl max-w-4xl w-full mx-4 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900/50 via-blue-800/30 to-blue-900/10 border-b border-blue-900/30 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-900/50 rounded-full">
                <HelpCircle className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">TraceMaster Help Center</h3>
                <p className="text-gray-400 text-sm">Comprehensive guides and documentation</p>
              </div>
            </div>
            <button
              onClick={() => setShowHelpPopup(false)}
              className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Search bar */}
        <div className="p-4 border-b border-gray-800 bg-gray-900/80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help topics..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>
        
        {/* Navigation path */}
        {activeSection !== 'main' && (
          <div className="px-5 py-3 bg-gray-800/50 border-b border-gray-800 flex items-center space-x-2">
            <button
              onClick={navigateBack}
              className="p-1 text-gray-400 hover:text-white rounded hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-gray-500 text-sm">
              Help Center / {activeSection.startsWith('guide-') ? 'Tool Guides / ' + activeSection.replace('guide-', '') : activeSection.replace(/-/g, ' ')}
            </span>
          </div>
        )}
        
        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {renderSection()}
        </div>
        
        {/* Footer */}
        <div className="bg-gray-900 border-t border-gray-800 px-5 py-4 flex justify-between">
          <div className="text-gray-500 text-sm">
            Last updated: March 2025
          </div>
          <button
            onClick={() => setShowHelpPopup(false)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
          >
            Close Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

// Help center section definitions
const helpSections = [
  {
    id: 'tool-guides',
    title: 'Tool Guides',
    description: 'Learn how to use each analysis tool effectively with step-by-step instructions',
    icon: <Terminal className="w-5 h-5 text-blue-400" />
  },
  {
    id: 'data-sources',
    title: 'Data Sources',
    description: 'Understand where our information comes from and how it\'s verified',
    icon: (<Database className="w-5 h-5 text-green-400" />)
  },
  {
    id: 'legal-compliance',
    title: 'Legal & Compliance',
    description: 'Important information about legal usage and privacy considerations',
    icon: (<ShieldAlert className="w-5 h-5 text-purple-400" />)
  },
  {
    id: 'advanced-techniques',
    title: 'Advanced Techniques',
    description: 'Expert-level methods for deeper investigation and analysis',
    icon: (<Zap className="w-5 h-5 text-yellow-400" />)
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Solutions for common issues and limitations you might encounter',
    icon: (<Settings className="w-5 h-5 text-red-400" />)
  },
  {
    id: 'tutorials',
    title: 'Video Tutorials',
    description: 'Watch comprehensive guides for visual learners',
    icon: (<Users className="w-5 h-5 text-orange-400" />)
  }
];

// Tool guides definitions
const toolGuides = [
  {
    id: 'email',
    title: 'Email Analysis',
    description: 'Trace and verify email senders, domains, and headers',
    icon: <Mail className="w-5 h-5 text-blue-400" />,
    fullDescription: 'The Email Analysis tool allows you to trace the origin and authenticity of email addresses by examining headers, domain information, and sender patterns.'
  },
  {
    id: 'phone',
    title: 'Phone Number Lookup',
    description: 'Gather information about phone numbers and carriers',
    icon: <Phone className="w-5 h-5 text-green-400" />
  },
  {
    id: 'ip',
    title: 'IP Address Tracing',
    description: 'Analyze IP addresses to determine geographic location and network details',
    icon: <Globe className="w-5 h-5 text-purple-400" />
  },
  {
    id: 'domain',
    title: 'Domain Intelligence',
    description: 'Research domain registration, history, and reputation',
    icon: <Server className="w-5 h-5 text-yellow-400" />
  }
];

// Email data points
const emailDataPoints = [
  {
    title: 'Domain Age & Registration',
    description: 'When the email domain was first registered and by whom',
    icon: <Clock className="w-4 h-4" />
  },
  {
    title: 'Server Location',
    description: 'Geographic location of the email server infrastructure',
    icon: <Globe className="w-4 h-4" />
  },
  {
    title: 'SPF/DKIM Verification',
    description: 'Authentication checks to verify sender legitimacy',
    icon: <Lock className="w-4 h-4" />
  },
  {
    title: 'Previous Breaches',
    description: 'Whether the email has appeared in known data breaches',
    icon: <AlertTriangle className="w-4 h-4" />
  },
  {
    title: 'Reputation Score',
    description: 'Overall trustworthiness based on multiple factors',
    icon: <FileText className="w-4 h-4" />
  },
  {
    title: 'Associated Accounts',
    description: 'Public profiles linked to this email address',
    icon: <Users className="w-4 h-4" />
  }
];

// Data sources
const dataSources = [
  {
    title: 'Domain & DNS Information',
    icon: <Globe className="w-5 h-5 text-blue-400" />,
    sources: [
      {
        name: 'WHOIS Databases',
        description: 'Domain registration information including owner contact details, registration dates, and hosting provider',
        updateFrequency: 'Daily',
        limitations: 'Many domains use privacy protection services'
      },
      {
        name: 'DNS Records',
        description: 'Domain name system records showing IP assignments, mail server configurations, and domain verification entries',
        updateFrequency: 'Real-time',
        limitations: 'None'
      },
      {
        name: 'Domain Reputation Services',
        description: 'Aggregated security scores and blacklist status from multiple security vendors',
        updateFrequency: 'Hourly',
        limitations: 'May lag behind for newly compromised domains'
      }
    ]
  },
  {
    title: 'Email Intelligence',
    icon: <Mail className="w-5 h-5 text-green-400" />,
    sources: [
      {
        name: 'Header Analysis Systems',
        description: 'Processing of technical email header information to trace routing and authentication',
        updateFrequency: 'Real-time',
        limitations: 'Can be spoofed by sophisticated actors'
      },
      {
        name: 'Breach Databases',
        description: 'Records of email addresses exposed in known data breaches and leaks',
        updateFrequency: 'Weekly',
        limitations: 'Only includes publicly disclosed breaches'
      }
    ]
  }
];

export default HelpCenter;